import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { RiskBand } from '../../../models/risk-band';
import { AgilityRiskService } from '../../../providers/agility-risk.service';
import { AddRemoveRiskBandsComponent } from '../modals/add-remove-risk-bands/add-remove-risk-bands.component';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { RiskFormula } from '../../../models/risk-formula';
import * as _ from 'lodash';
import { AgilityRiskFormulaHelperService } from '../../../providers/agility-risk-formula-helper.service';
import { Subscription } from 'rxjs';
import { AgilityRiskMessageService } from '../../../providers/agility-risk-message.service';

@Component({
  selector: 'lib-risk-band-slider',
  templateUrl: './risk-band-slider.component.html',
  styleUrls: ['./risk-band-slider.component.scss']
})
export class RiskBandSliderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formulaId: string;
  @Input() rounding: boolean;
  sliderTrack: any;
  minGap = 0;
  riskBands: RiskBand[];
  minRange = '0';
  showMaxRange: any;
  maxRange: any;
  copy: RiskBand[];
  showBands: any[];
  deepCopy: any[];
  deepCopySnapshot: any[];
  snapshot: any[];
  background: string;

  formulaChangedSubscription: Subscription;

  constructor(
    private agilityRiskService: AgilityRiskService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private messageService: AgilityRiskMessageService,
    private formulaHelper: AgilityRiskFormulaHelperService) {
  }

  ngOnInit() {

    this.getRiskBands();
    this.formulaChangedSubscription = this.formulaHelper.subscribeToFormulaChangedAndValid(this.onFormulaChangedAndValid.bind(this));
  }

  ngOnDestroy() {
    this.formulaChangedSubscription?.unsubscribe();
  }

  ngOnChanges() {
    if (this.riskBands) {
      this.changeRound();
      this.getRiskBands();
    }
  }

  getRiskBands(riskBands?: RiskBand[]) {
    if (!riskBands) {
      this.agilityRiskService.getRiskFormulaBands(this.formulaId).subscribe((riskBands: Array<RiskBand>) => {
        this.initializeSlider(riskBands);
      });
    } else {
      this.initializeSlider(riskBands);
    }
  }

  initializeSlider(riskBands: RiskBand[]) {
    this.riskBands = riskBands.sort((a, b) => a.numericValue - b.numericValue);
    this.maxRange = this.showMaxRange = Math.max(...this.riskBands.map((el) => el.numericValue));
    this.copy = [...this.riskBands].sort((a, b) => a.numericValue - b.numericValue);
    this.snapshot = _.cloneDeep([...this.riskBands]);
    this.sliderTrack = document.querySelector('.slider-track') as HTMLElement;
    this.changeRound(riskBands);
    this.fillColor();
    this.copy.pop();
    this.deepCopy = _.cloneDeep(
      this.riskBands.map((el) => {
        return {...el, prev: el.numericValue};
      })
    );
    this.deepCopySnapshot = _.cloneDeep([...this.deepCopy]);
  }

  openRiskBandDialog() {
    const ref = this.dialogService.open(AddRemoveRiskBandsComponent, {
      header: this.translateService.instant('assessment.survey.riskBands'),
      width: '70%',
      data: {formulaId: this.formulaId}
    });
    ref.onClose.subscribe(_ => this.getRiskBands());
  }


  fillColor(ref?: HTMLInputElement) {
    const background = 'linear-gradient(to right,';
    let right: boolean;

    if (ref) {
      this.deepCopy[ref.id].numericValue = Number(ref.value);

      if (this.deepCopy[ref.id].numericValue - this.deepCopy[ref.id].prev > 0) {
        right = true;
      } else {
        right = false;
      }
      this.deepCopy[ref.id].prev = this.deepCopy[ref.id].numericValue;

    }

    this.checkPosition(right, ref);
    this.updateColor();
    this.changeRound();
  }

  updateColor() {
    this.background = 'linear-gradient(to right,';
    this.riskBands.forEach((el, i, e) => {
      if (e.length <= 1) {
        this.background += `${el.color ? el.color : 'white'} 0%, ${el.color ? el.color : 'white'} 100%)`;
      } else {
        if (e[i - 1] == undefined) {
          this.background += `${el.color ? el.color : 'white'} 0%, ${el.color ? el.color : 'white'} ${
            Math.floor((el.numericValue / this.maxRange) * 100)
          }%,`;

        } else {
          this.background +=
            i + 1 !== this.riskBands.length
              ? `
      ${el.color ? el.color : 'white'} ${Math.floor((e[i - 1].numericValue / this.maxRange) * 100)}%, ${el.color ? el.color : 'white'} ${Math.floor((el.numericValue / this.maxRange) * 100)}%,`
              : `${el.color ? el.color : 'white'} ${Math.floor((e[i - 1].numericValue / this.maxRange) * 100)}%, ${
                el.color ? el.color : 'white'
              } ${Math.floor((el.numericValue / this.maxRange) * 100)}%)`;

        }
      }
    });
    this.sliderTrack.style.background = this.background;
  }

  changeRound(riskBands?: RiskBand[]) {
    if (riskBands) {
      this.showBands = [...riskBands].sort((a, b) => a.numericValue - b.numericValue);
    }
    this.showBands = [...this.riskBands];
    if (this.rounding) {
      this.showBands = this.showBands.map((el) => {
        return {
          ...el,
          numericValue: `${el.numericValue}.99`,

          numeric: `${el.numericValue + 1}.00`
        };
      });
      this.minRange = '0.00';
      this.showMaxRange = `${this.maxRange}.99`;
    } else {
      this.showBands = this.showBands.map((el) => {
        return {
          ...el,
          numericValue: el.numericValue,
          numeric: el.numericValue + 1
        };
      });
      this.minRange = '0';
      this.showMaxRange = this.maxRange;
    }
  }

  checkPosition(right, ref: HTMLInputElement) {
    if (right) {
      this.copy.forEach((el, i, e) => {
        const prev = e[i - 1];
        const next = e[i + 1];

        if (next) {
          if (
            Number(el.numericValue) - Number(next.numericValue) ==
            this.minGap
          ) {
            next.numericValue = Number(next.numericValue) + 1;
            this.deepCopy[i + 1].numericValue =
              this.deepCopy[i + 1].numericValue + 1;
            this.deepCopy[i + 1].prev = this.deepCopy[i + 1].prev + 1;
          }
        }
      });
    } else {
      let i = Number(ref?.id);
      while (i > 0) {
        const prev = this.copy[i - 1];
        const current = this.copy[i];

        if (prev) {
          if (prev.numericValue - current.numericValue == this.minGap) {
            prev.numericValue = prev.numericValue - 1;
            this.deepCopy[i - 1].numericValue =
              this.deepCopy[i - 1].numericValue - 1;
            this.deepCopy[i - 1].prev = this.deepCopy[i - 1].prev - 1;
          }
        }
        i--;
      }
    }
  }

  checkValidation() {
    let overlap = false;
    this.riskBands.forEach((current, index, array) => {
      const prev = array[index - 1];
      const next = array[index + 1];

      if (prev) {
        if (current.numericValue - prev.numericValue <= 0) {
          overlap = true;
        }
      }
      if (next) {
        if (next.numericValue - current.numericValue <= 0) {
          overlap = true;
        }
      }
      if (current.numericValue == 0) {
        overlap = true;
      }
    });

    if (!overlap) {
      this.snapshot = _.cloneDeep([...this.riskBands]);
      this.deepCopySnapshot = _.cloneDeep([...this.deepCopy]);
    } else {
      this.validationMessage();
      this.riskBands = [...this.snapshot];
      this.deepCopy = _.cloneDeep([...this.deepCopySnapshot]);
      const copy = [...this.snapshot];
      copy.pop();
      this.copy = copy;
      this.snapshot = _.cloneDeep([...this.riskBands]);
      this.changeRound();
      this.updateColor();
    }
  }

  validationMessage() {
    this.messageService.error(this.translateService.instant('assessment.survey.validationError'), this.translateService.instant('assessment.survey.cantOverlapBands'));
  }

  savePositions() {
    this.agilityRiskService.putRiskBandsPosition(this.riskBands).subscribe({
      next: (response) => {
        // TODO: is there gonna be a toast ?

      }, error: (err) => {
        // TODO: is there gonna be a toast ?
      }
    });
  }

  onFormulaChangedAndValid(formula: RiskFormula) {
    this.agilityRiskService.getRiskBandRecalculation(formula).subscribe((riskBands: RiskBand[]) => {
      this.getRiskBands(riskBands);
    });
  }
}
